"use client"
import type { CaseStudyContent } from "@/lib/case-study"
import Link from "next/link"
import Image from "next/image"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import { Footer } from "@/components/layout/Footer"
import { Emphasis } from "@/components/cms/Emphasis"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
// Small text (11–14px) in the brand orange sits at 4.21:1 on the navy — below the
// 4.5:1 WCAG AA floor Lighthouse flagged on this page. Large fills keep ACCENT; text uses this.
const ACCENT_TEXT = "#E06B2E"
const MUTED = "rgba(255,255,255,0.6)"
const BORDER = "rgba(255,255,255,0.08)"

const HERO = "/work/florida-thrift/home.jpg"


export function CaseClient({ content }: { content: CaseStudyContent }) {
  const images = content.images
  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      {/* Back */}
      <div style={{ position: "fixed", top: 80, left: "clamp(20px, 4vw, 48px)", zIndex: 10 }}>
        <Link href="/work" style={{ fontSize: 13, color: MUTED, textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
          ← Back to Work
        </Link>
      </div>

      {/* Hero */}
      <section style={{ padding: "clamp(100px, 16vw, 140px) clamp(20px, 4vw, 48px) 0", maxWidth: 900, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 24 }}>
            <span style={{ width: 32, height: 1, background: ACCENT, flexShrink: 0 }} />
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{content.eyebrow}</span>
          </div>
          <h1 style={{ fontSize: "clamp(36px, 6vw, 80px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.9, marginBottom: 24 }}>
            {content.titleLine1}<br /><span style={{ color: ACCENT }}>{content.titleLine2}</span>
          </h1>
          <p style={{ color: MUTED, fontSize: "clamp(15px, 1.3vw, 18px)", lineHeight: 1.7, maxWidth: 640, marginBottom: 32 }}>
            {content.intro}
          </p>
          <a href={content.liveUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: ACCENT_TEXT, textDecoration: "none", border: `1px solid ${ACCENT_TEXT}`, borderRadius: 8, padding: "10px 20px", marginBottom: 48 }}>
            {content.liveLabel}
          </a>
        </ScrollReveal>

        {/* Hero image */}
        <ScrollReveal effect="fade-up" delay={0.1}>
          <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: 16, overflow: "hidden", marginBottom: 64 }}>
            <Image src={HERO} alt="Florida Thrift website" fill priority style={{ objectFit: "cover", objectPosition: "top" }} sizes="900px" />
          </div>
        </ScrollReveal>
      </section>

      {/* Content */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px) clamp(60px, 8vw, 100px)", maxWidth: 900, margin: "0 auto" }}>
        {/* Challenge */}
        <ScrollReveal effect="fade-up">
          <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 48, marginBottom: 48 }}>
            <h2 style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT_TEXT, marginBottom: 16 }}>{content.challengeHeading}</h2>
            <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.75 }}>
              <Emphasis text={content.challenge} />
            </p>
          </div>
        </ScrollReveal>

        {/* Solution */}
        <ScrollReveal effect="fade-up">
          <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 48, marginBottom: 48 }}>
            <h2 style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT_TEXT, marginBottom: 16 }}>{content.solutionHeading}</h2>
            <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.75 }}>
              <Emphasis text={content.solution} />
            </p>
            <ul style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              {content.solutionPoints.map(item => (
                <li key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start", color: MUTED, fontSize: 15 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, flexShrink: 0, marginTop: 7 }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </ScrollReveal>

        {/* Outcomes */}
        <ScrollReveal effect="fade-up">
          <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 48, marginBottom: 48 }}>
            <h2 style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT_TEXT, marginBottom: 16 }}>{content.outcomesHeading}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
              {content.outcomes.map(o => (
                <div key={o.label} style={{ padding: "20px 24px", border: `1px solid ${BORDER}`, borderRadius: 12, background: "rgba(255,255,255,0.02)" }}>
                  <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{o.label}</p>
                  <p style={{ color: MUTED, fontSize: 13, lineHeight: 1.6 }}>{o.sub}</p>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginTop: 20 }}>
              Rankings are dated: they&apos;re what the search showed on that day, and they move over time.
            </p>
          </div>
        </ScrollReveal>

        {/* Gallery */}
        <ScrollReveal effect="fade-up">
          <h2 style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT_TEXT, marginBottom: 24 }}>{content.galleryHeading}</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {images.map(img => (
              <div key={img.src} style={{ position: "relative", aspectRatio: "4/3", borderRadius: 12, overflow: "hidden", background: "#0d1117" }}>
                <Image src={img.src} alt={img.alt ?? ""} fill style={{ objectFit: "cover", objectPosition: "top" }} sizes="(max-width: 768px) 100vw, 400px" />
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Nav */}
        {/* Onward links: the package this kind of build ships as, and (for trades) the
            contractors page. Case studies used to dead-end into the next case study. */}
        <p style={{ marginTop: 56, padding: "20px 24px", borderRadius: 14, border: `1px solid ${BORDER}`, color: MUTED, fontSize: 14, lineHeight: 1.8 }}>
          Want this for your business?{" "}
          <Link href="/services" style={{ color: ACCENT_TEXT, textDecoration: "none", fontWeight: 600 }}>See the Website + AI Assistant package →</Link>
          {" "}· <Link href="/estimate" style={{ color: "#fff", textDecoration: "underline", textUnderlineOffset: 4 }}>Get an instant estimate</Link>
        </p>

        <div style={{ marginTop: 64, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <Link href="/work/case-jmt-handyman" style={{ fontSize: 14, color: MUTED, textDecoration: "none" }}>← JMT Handyman Services</Link>
          <Link href="/work" style={{ fontSize: 14, color: ACCENT_TEXT, textDecoration: "none", fontWeight: 600 }}>All Work →</Link>
        </div>
      </section>

      <Footer />
    </main>
  )
}
